import { useState, useEffect, useCallback, useMemo } from "react"
import { useFilteredVehicleOptions } from "./stockFilterHook"
import { modelToModelIdsNumber } from "utils/modelUtils"

export const useVehicleForm = ({ stockResponse, isEdit, formik }) => {
  const [params, setParams] = useState({ brand: "", model: "" })
  const [allFeatures, setAllFeatures] = useState([])

  // Update params when editing a stock
  useEffect(() => {
    if (stockResponse?.id) {
      setParams({
        brand: stockResponse?.brand?.slug ?? "",
        model: stockResponse?.model?.slug ?? "",
      })
    }
  }, [stockResponse?.id])

  // Handler to update params
  const handleParams = useCallback((name, value) => {
    setParams(prev => {
      if (prev[name] === value) return prev
      return { ...prev, [name]: value }
    })
  }, [])

  // Fetch filtered vehicle options
  const {
    isLoading: optionLoading,
    brands: brandsData,
    models: modelsData,
    packages: packagesData,
    engineCodes: engineCodesData,
    chassisCodes: chassisCodesData,
    engineCCs: engineCcsData,
    features: featuresData,
    showrooms: showroomsData,
    raw: rawData,
  } = useFilteredVehicleOptions(params)

  // Extract raw data
  const yearsData = rawData?.years ?? []
  const extColorsData = rawData?.exterior_colors ?? []
  const intColorsData = rawData?.interior_colors ?? []
  const statusesData = rawData?.statuses ?? []
  const auctionGradesData = rawData?.auction_grades ?? []
  const transmissionsData = rawData?.transmissions ?? []
  const engineTypesData = rawData?.engine_types ?? []
  const fuelTypesData = rawData?.fuel_types ?? []
  const keyTypesData = rawData?.key_types ?? []
  const locationsData = rawData?.locations ?? []

  // Set all features from raw data
  useEffect(() => {
    if (rawData?.features?.length) {
      setAllFeatures(prev =>
        JSON.stringify(prev) === JSON.stringify(rawData.features)
          ? prev
          : rawData.features
      )
    }
  }, [rawData?.features])

  // Automatically check model-associated features when adding new stock
  useEffect(() => {
    
    if (!isEdit && featuresData?.length) {
      const modelFeatureIds = modelToModelIdsNumber(featuresData)
      
      formik.setFieldValue("selectedFeatures", modelFeatureIds, false) // `false` avoids validation re-run
    }
  }, [featuresData, isEdit]) // removed formik dep

  const handleBrandChange = useCallback(
    brandId => {
      formik.handleChange(brandId)
      const selectedBrand = brandsData?.find(
        brand => brand?.id === Number(brandId)
      )
      handleParams("brand", selectedBrand?.slug ?? "")
    },
    [brandsData, handleParams, formik]
  )

  const handleModelChange = useCallback(
    modelId => {
      formik.handleChange(modelId)
      const selectedModel = modelsData?.find(
        model => model?.id === Number(modelId)
      )
      handleParams("model", selectedModel?.slug ?? "")
    },
    [modelsData, handleParams, formik]
  )

  return useMemo(
    () => ({
      params,
      handleParams,
      handleBrandChange,
      handleModelChange,
      optionLoading,
      brandsData,
      modelsData,
      packagesData,
      engineCodesData,
      chassisCodesData,
      engineCcsData,
      featuresData,
      allFeatures,
      showroomsData,
      rawData,
      yearsData,
      extColorsData,
      intColorsData,
      statusesData,
      auctionGradesData,
      transmissionsData,
      engineTypesData,
      fuelTypesData,
      keyTypesData,
      locationsData,
    }),
    [
      params,
      handleParams,
      handleBrandChange,
      handleModelChange,
      optionLoading,
      brandsData,
      modelsData,
      packagesData,
      engineCodesData,
      chassisCodesData,
      engineCcsData,
      featuresData,
      allFeatures,
      showroomsData,
      rawData,
      yearsData,
      extColorsData,
      intColorsData,
      statusesData,
      auctionGradesData,
      transmissionsData,
      engineTypesData,
      fuelTypesData,
      keyTypesData,
      locationsData,
    ]
  )
}
